import React, { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { fetchItemTransactions } from '../../../../services/Http/Api';
import { useCurrency } from '../../../../context/CurrencyProvider';
import { DEFAULT_CURRENCY } from '../../../../utils/currencyFormatter';

const ItemTransactionsTable = ({ itemId }) => {
    const { t } = useTranslation();
    const { getDisplayAmount, formatResolvedAmount } = useCurrency();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const loadTransactions = async () => {
            setLoading(true);
            try {
                const data = await fetchItemTransactions(itemId);
                setTransactions(Array.isArray(data) ? data : data.data || []);
            } catch (error) {
                console.error('Error fetching item transactions:', error);
            } finally {
                setLoading(false);
            }
        };

        loadTransactions();
    }, [itemId]);

    const formatCurrency = (
        eurValue,
        buyerLocalValue = null,
        sellerLocalValue = null,
        buyerCurrencyId = null,
        sellerCurrencyId = null,
    ) => {
        const numberValue = Number(eurValue);
        if (!Number.isFinite(numberValue)) return t('Items.TransactionsTable.n_a');

        const buyerDisplay = getDisplayAmount(numberValue, buyerLocalValue, null, buyerCurrencyId);
        if (buyerDisplay.currency !== DEFAULT_CURRENCY) {
            return buyerDisplay.formatted;
        }

        const sellerDisplay = getDisplayAmount(numberValue, sellerLocalValue, null, sellerCurrencyId);
        if (sellerDisplay.currency !== DEFAULT_CURRENCY) {
            return sellerDisplay.formatted;
        }

        return formatResolvedAmount({ eurAmount: numberValue });
    };

    const parseJsonField = (value) => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        try {
            return JSON.parse(value);
        } catch (error) {
            return null;
        }
    };

    return (
        <div>
            <SectionHeader>
                <h5>{t('Items.TransactionsTable.title')}</h5>
            </SectionHeader>
            {loading ? (
                <SpinnerWrapper>
                    <Spinner color="primary" />
                </SpinnerWrapper>
            ) : transactions.length === 0 ? (
                <EmptyState>{t('Items.TransactionsTable.no_transactions')}</EmptyState>
            ) : (
                <Timeline>
                    {transactions.map((row, index) => {
                        const buyerName =
                            `${row.buyer?.name ?? ''} ${row.buyer?.surname ?? ''}`.trim() ||
                            row.buyer?.name ||
                            t('Items.TransactionsTable.n_a');
                        const sellerName =
                            `${row.seller?.name ?? ''} ${row.seller?.surname ?? ''}`.trim() ||
                            row.seller?.name ||
                            t('Items.TransactionsTable.n_a');
                        const shippingDetails = parseJsonField(row.shipping_details);
                        const warrantyDetails = parseJsonField(row.warranty_details);
                        const counterpartyLabel = row.organization_direction === 'bought'
                            ? `${t('Items.TransactionsTable.counterparty_from')} ${sellerName}`
                            : row.organization_direction === 'sold'
                                ? `${t('Items.TransactionsTable.counterparty_to')} ${buyerName}`
                                : null;
                        const isBought = row.organization_direction === 'bought';
                        const directionLabel = isBought
                            ? t('Items.TransactionsTable.direction_bought')
                            : row.organization_direction === 'sold'
                                ? t('Items.TransactionsTable.direction_sold')
                                : null;
                        return (
                            <TimelineItem key={row.id || `${row.date_of_transaction}-${index}`}>
                                <TimelineMarker>
                                    <TimelineDot />
                                    {index < transactions.length - 1 && <TimelineLine />}
                                </TimelineMarker>
                                <TimelineContent>
                                    <TimelineHeader>
                                        <div>
                                            <TimelineDate>
                                                {row.date_of_transaction
                                                    ? new Date(row.date_of_transaction).toLocaleDateString()
                                                    : t('Items.TransactionsTable.n_a')}
                                            </TimelineDate>
                                            <TimelineTitle>
                                                {directionLabel || row.transaction_type || t('Items.TransactionsTable.n_a')}
                                            </TimelineTitle>
                                        </div>
                                        <TimelineBadge>
                                            {row.quantity ? `${row.quantity}x` : t('Items.TransactionsTable.n_a')}
                                        </TimelineBadge>
                                    </TimelineHeader>
                                    <TimelineSummary>
                                        <span>{t('Items.TransactionsTable.buyer')}:</span>
                                        <strong>{buyerName}</strong>
                                        <span>{t('Items.TransactionsTable.venue')}:</span>
                                        <strong>{row.venue?.name || t('Items.TransactionsTable.n_a')}</strong>
                                        {counterpartyLabel && (
                                            <>
                                                <span>{t('Items.TransactionsTable.counterparty')}:</span>
                                                <strong>{counterpartyLabel}</strong>
                                            </>
                                        )}
                                    </TimelineSummary>
                                    <TimelineMeta>
                                        <MetaBlock>
                                            <span>
                                                {isBought
                                                    ? t('Items.TransactionsTable.price_paid')
                                                    : t('Items.TransactionsTable.sale_value')}
                                            </span>
                                            <strong>
                                                {isBought
                                                    ? formatCurrency(row.price, row.price_buyer_local, row.price_seller_local, row.buyer_currency_id, row.seller_currency_id)
                                                    : formatCurrency(row.price, row.price_buyer_local, row.price_seller_local, row.buyer_currency_id, row.seller_currency_id)}
                                            </strong>
                                        </MetaBlock>
                                        {row.cost_price != null && (
                                            <MetaBlock>
                                                <span>{t('Items.TransactionsTable.cost_price', 'Cost Price')}</span>
                                                <strong>{formatCurrency(row.cost_price, null, row.cost_price_seller_local, null, row.seller_currency_id)}</strong>
                                            </MetaBlock>
                                        )}
                                        {row.gross_margin != null && (
                                            <MetaBlock highlight={row.gross_margin > 0}>
                                                <span>{t('Items.TransactionsTable.gross_margin', 'Gross Margin')}</span>
                                                <strong>{formatCurrency(row.gross_margin, null, row.gross_margin_seller_local, null, row.seller_currency_id)}</strong>
                                            </MetaBlock>
                                        )}
                                        <MetaBlock>
                                            <span>{t('Items.TransactionsTable.net_revenue', 'Net Revenue')}</span>
                                            <strong>{formatCurrency(row.revenue, null, row.revenue_seller_local, null, row.seller_currency_id)}</strong>
                                        </MetaBlock>
                                        <MetaBlock>
                                            <span>{t('Items.TransactionsTable.payment_method')}</span>
                                            <strong>{row.payment_method || t('Items.TransactionsTable.n_a')}</strong>
                                        </MetaBlock>
                                        <MetaBlock>
                                            <span>{t('Items.TransactionsTable.shipping_cost')}</span>
                                            <strong>
                                                {shippingDetails?.cost ? formatCurrency(shippingDetails.cost) : t('Items.TransactionsTable.n_a')}
                                            </strong>
                                        </MetaBlock>
                                        <MetaBlock>
                                            <span>{t('Items.TransactionsTable.warranty')}</span>
                                            <strong>{warrantyDetails?.warranty_period || t('Items.TransactionsTable.n_a')}</strong>
                                        </MetaBlock>
                                    </TimelineMeta>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    })}
                </Timeline>
            )}
        </div>
    );
};

export default ItemTransactionsTable;

const SectionHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
`;

const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 1.5rem 0;
`;

const EmptyState = styled.div`
    padding: 1.5rem;
    border-radius: 12px;
    background: rgba(148, 163, 184, 0.12);
    text-align: center;
    color: #6b7280;
`;

const Timeline = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.125rem;
`;

const TimelineItem = styled.div`
    display: grid;
    grid-template-columns: 20px 1fr;
    gap: 1rem;
`;

const TimelineMarker = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const TimelineDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(130, 22, 146, 1);
    box-shadow: 0 0 0 6px rgba(130, 22, 146, 0.12);
`;

const TimelineLine = styled.div`
    flex: 1;
    width: 2px;
    background: rgba(148, 163, 184, 0.3);
    margin-top: 0.5rem;
`;

const TimelineContent = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 1rem;
    border: 1px solid rgba(148, 163, 184, 0.2);
    box-shadow: 0 8px 18px rgba(17, 24, 39, 0.06);
`;

const TimelineHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
`;

const TimelineDate = styled.div`
    font-size: 0.875rem;
    color: #6b7280;
`;

const TimelineTitle = styled.div`
    font-size: 1rem;
    font-weight: 600;
    margin-top: 0.25rem;
`;

const TimelineBadge = styled.div`
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    background: rgba(130, 22, 146, 0.12);
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    font-size: 0.8125rem;
`;

const TimelineSummary = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.5rem 1rem;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;

    span {
        color: #6b7280;
    }
`;

const TimelineMeta = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.625rem;
`;

const MetaBlock = styled.div`
    background: ${({ highlight }) => highlight ? 'rgba(34, 197, 94, 0.1)' : 'rgba(130, 22, 146, 0.06)'};
    border-radius: 10px;
    padding: 0.625rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;

    span {
        color: #6b7280;
    }

    strong {
        color: ${({ highlight }) => highlight ? '#16a34a' : 'inherit'};
    }
`;
